/* ContractsCrudCard.tsx
    Une card affichant la gestion des contrats
    Utilise le composant GenericCrudCard pour lister, ajouter, gérer et supprimer les éléments
    Utilise l'API correspondante pour les opérations de données
*/

import React from 'react';
import { GenericCrudCard } from 'common';
import { ContractData, contractsApi } from 'features/contracts';
import { faRepeat } from '@fortawesome/free-solid-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';

interface ContractsCrudCardProps {
	size?: 'sm' | 'md' | 'lg';
	returnPath?: string;
	returnLabel?: string;
	returnIcon?: IconDefinition;
	returnInvert?: boolean;
	className?: string;
	/** Affiche ou masque l'icône dans l'en-tête de la card. Défaut : true */
	showIcon?: boolean;
	/** Masque le bouton retour. Utile quand la card est intégrée dans une page avec son propre bouton retour. Défaut : false */
	hideReturn?: boolean;
}

// ContractsCrudCard component
export const ContractsCrudCard: React.FC<ContractsCrudCardProps> = ({
	size = 'sm',
	returnPath = '/app/contracts',
	returnLabel,
	returnIcon,
	returnInvert = false,
	className,
	showIcon = true,
	hideReturn = false,
}) => {
	// Détermination si on utilise la version étendue (size !== 'sm')
	const extended = size !== 'sm';

	// Définition des colonnes du tableau
	const columns = [
		// Colonne "ID" uniquement si extended
		...(extended
			? [{ label: 'ID', field: 'id', render: (a: ContractData) => a.id }]
			: []),
		// Colonne "Libellé"
		{ label: 'Libellé', field: 'label', render: (a: ContractData) => a.label },
		// Colonne Fournisseur
		{
			label: 'Fournisseur',
			field: 'supplier.label',
			render: (a: ContractData) => a.supplier?.label ?? '—',
			exportValue: (a: ContractData) => a.supplier?.label ?? '',
		},
		// Colonne Périodicité
		{
			label: 'Périodicité',
			field: 'periodicity',
			render: (a: ContractData) => a.periodicity ?? '—',
		},
		// Colonne Montant estimé HT (formaté)
		{
			label: 'Montant HT',
			field: 'estimatedAmountHt',
			render: (a: ContractData) =>
				a.estimatedAmountHt != null
					? new Intl.NumberFormat('fr-FR', {
							style: 'currency',
							currency: 'EUR',
						}).format(Number(a.estimatedAmountHt))
					: '—',
			exportValue: (a: ContractData) => Number(a.estimatedAmountHt ?? 0),
		},
		// Colonne Date de début — export uniquement (tableau trop chargé)
		// Colonne Date de fin — export uniquement (tableau trop chargé)
		// Colonne Centre de coût uniquement si extended
		...(extended
			? [
					{
						label: 'Centre de coût',
						field: 'costCenter.label',
						render: (a: ContractData) => a.costCenter?.label ?? '—',
						exportValue: (a: ContractData) => a.costCenter?.label ?? '',
					},
				]
			: []),
		// Colonne Statut actif/inactif — toujours visible
		{
			label: 'Actif',
			field: 'active',
			render: (a: ContractData) => (a.active ? 'Oui' : 'Non'),
		},
	];

	// Colonnes export uniquement : dates de début et fin (trop volumineuses pour le tableau)
	const exportOnlyColumns = [
		{
			label: 'Date de début',
			exportValue: (a: ContractData) =>
				a.startDate ? new Date(a.startDate).toLocaleDateString('fr-FR') : '',
		},
		{
			label: 'Date de fin',
			exportValue: (a: ContractData) =>
				a.endDate ? new Date(a.endDate).toLocaleDateString('fr-FR') : '',
		},
	];

	return (
		<GenericCrudCard<ContractData>
			queryKey="contracts"
			title={
				size === 'sm'
					? 'Contrats fournisseurs'
					: 'Liste des contrats fournisseurs'
			}
			fetchData={(token, options) => contractsApi.listPaginated(token, options)}
			deleteItem={contractsApi.delete}
			tableColumns={columns}
			exportOnlyColumns={exportOnlyColumns}
			addButtonLabel="Ajouter un contrat"
			getAddPath={() => '/app/contracts/new'}
			getEditPath={(id) => `/app/contracts/${id}/edit`}
			emptyMessage="Aucun contrat trouvé."
			getReturnPath={hideReturn ? undefined : () => returnPath}
			returnLabel={returnLabel}
			returnIcon={returnIcon}
			returnInvert={returnInvert}
			size={size}
			icon={size === 'sm' ? faRepeat : undefined}
			className={className}
			showIcon={showIcon}
		/>
	);
};

export default ContractsCrudCard;
